/** The terms, in plain language: xchange holds nothing; every trade settles through escrow. */
export function Terms({ onBack }: { onBack: () => void }) {
  const header = (
    <div className="header">
      <button className="ghost" onClick={onBack}>
        ‹ Back
      </button>
      <h2 className="title">Terms of use</h2>
      <span style={{ width: 56 }} />
    </div>
  );
  return (
    <div className="screen">
      {header}

      <h3 className="title">What xchange is</h3>
      <p>
        xchange is a peer-to-peer exchange for Gaju, USDC (Solana) and XGE. You place a bid to buy
        or an offer to sell. When a bid and an offer meet, the two sides settle through escrow smart
        contracts on Gajumaru and Solana. xchange matches bids and offers and coordinates
        settlement. It never holds your keys, and what is in your wallet only you can move.
      </p>

      <h3 className="title">Your keys, your funds</h3>
      <p>
        Your wallet is created on your phone, with a 12-word recovery phrase that never leaves it.
        It is protected by your PIN and backed up, encrypted, in your own Telegram. xchange never
        sees your keys or your phrase. The same phrase opens the same wallet in any standard Solana
        or aeternity wallet.
      </p>
      <p>
        Your recovery phrase is the only way to open your wallet on another phone, or on this one
        after Telegram is deleted or reinstalled. The app shows it when the wallet is made and asks
        you to type it back; write it down then and keep it offline. Telegram's backup is a
        convenience, not a guarantee. If the phrase is lost, nobody, including xchange, can recover
        the wallet or anything in it. Starting a new wallet creates new addresses; the old wallet
        and its balance stay where they are and cannot be reached without its phrase.
      </p>
      <p>
        In a browser, at app.xchangehq.com, the wallet on that device is your sign-in: your key
        signs a one-time challenge and the server recognises the address. There is no password and
        nothing on the server to reset. The same twelve words open the same account there and in
        Telegram. A browser keeps no cloud backup, and a wallet in a browser is only as safe as that
        browser profile and its PIN. Only app.xchangehq.com ever asks for your recovery phrase.
      </p>

      <h3 className="title">How a trade works</h3>
      <p>
        When you place a bid or an offer, your side goes into an escrow contract on its own chain,
        signed by you. When it meets a matching offer or bid, the other side is already in escrow
        too. Once both deposits are final, the contracts release each side to the other. Your
        deposit is released to the person you are swapping with, or refunded to you. xchange's
        coordinator key tells the contract who that person is; until a second, independent check on
        that key is added, you rely on it to name the right person.
      </p>
      <p>
        You can cancel a bid or an offer at any time for whatever has not filled yet, and that part
        comes straight back. Anything left unfilled refunds itself after 7 days. A settled trade is
        on chain and cannot be reversed.
      </p>

      <h3 className="title">Fees</h3>
      <p>
        0.25 percent of what each side receives, taken by the escrow contract when the trade
        settles: the buyer of Gaju pays 0.25 percent of the Gaju they receive, the seller pays 0.25
        percent of the USDC they receive. It is inside the amount the screen shows you before you
        confirm. Placing an order that deposits USDC or XGE on Solana carries a network charge of
        about a cent, in the asset deposited, paid with the deposit to xchange's fee wallet: it
        covers the transaction fees the order costs on that chain, no more. An order that deposits
        Gaju carries none; its gas is your own. Receiving is free. Sending USDC or XGE from your
        wallet costs a small xchange fee, 0.05 percent and less on large amounts, and, when the
        address has never held that token, the cost of opening its account on Solana, which xchange
        pays on your behalf. The fee is shown before you enter your PIN and is paid in the same
        transaction as the send. xchange pays the Solana network fee for you. Sending Gaju has no
        xchange fee; the network's small fee comes out of the Gaju you send.
      </p>

      <h3 className="title">XGE rewards</h3>
      <p>
        Every day for three years, xchange shares a fixed pool of XGE among everyone who settled a
        trade that day, in proportion to the dollars they swapped. Bringing a friend earns a fixed
        amount for each of you once they have swapped. There are caps per person per day and for the
        whole three years, a limit for each dollar swapped, and nothing for swapping with yourself.
        Everyone earns under the same rules, including the people who build xchange. The rules can
        change for the days ahead, never for days already closed. Rewards are paid to your own
        wallet and shown in your history with the reason. XGE is not an investment, and xchange
        never sets its price.
      </p>

      <h3 className="title">Sending</h3>
      <p>
        A send goes out the moment you enter your PIN and cannot be recalled. Only send Gaju to
        Gajumaru addresses and USDC or XGE to Solana addresses. The first time a Solana address
        receives a token, the network charges about half a dollar to open its account, and that
        comes out of what you send. You are responsible for the address you send to.
      </p>

      <h3 className="title">What you accept</h3>
      <p>
        Prices are set by the people who buy and sell, not by xchange, and can move. xchange does
        not give advice, does not guarantee that anyone will take your offer, and is provided as it
        is. XGE is a utility token for fees and rewards, not an investment. Use xchange only where
        it is legal for you to do so.
      </p>
    </div>
  );
}
